/**
 * Admin API client for the human review queue.
 *
 * @module adminHumanReviewApi
 */

import { apiGet, apiPost } from '@/lib/apiUtils';
import type { ReviewModule, ReviewPriority, ReviewResult, ReviewStatus } from '@/lib/humanReviewsApi';

export interface AdminReviewListItem {
  readonly id: string;
  readonly userId?: string | null;
  readonly userName?: string | null;
  readonly module: ReviewModule;
  readonly status: ReviewStatus;
  readonly priority: ReviewPriority;
  readonly attachedQuestion: string;
  readonly assignedReviewerId?: string | null;
  readonly assignedReviewerName?: string | null;
  readonly createdAt: string;
}

export interface AdminReviewListResponse {
  readonly items: AdminReviewListItem[];
  readonly total: number;
  readonly page: number;
  readonly totalPages: number;
  readonly limit: number;
}

export interface AdminReviewDetail {
  readonly id: string;
  readonly userId?: string | null;
  readonly userName?: string | null;
  readonly module: ReviewModule;
  readonly status: ReviewStatus;
  readonly priority: ReviewPriority;
  readonly attachedQuestion: string;
  readonly notes?: string | null;
  readonly dislikeReasonCodes?: string[] | null;
  readonly dislikeFreeText?: string | null;
  readonly targetAnswerText?: string | null;
  readonly targetAnswerAudioUrl?: string | null;
  readonly aiFeedback?: string | null;
  readonly assignedReviewerId?: string | null;
  readonly assignedReviewerName?: string | null;
  readonly createdAt: string;
  readonly completedAt?: string | null;
  readonly result?: ReviewResult | null;
}

export interface CompleteReviewPayload {
  readonly verifiedBand: string;
  readonly readinessVerdict: string;
  readonly prioritizedFixes: string[];
  readonly attachedQuestionAnswer: string;
}

export interface AdminReviewFilters {
  readonly status?: string;
  readonly module?: string;
  readonly priority?: string;
  readonly mine?: boolean;
  readonly page?: number;
}

export async function getAdminHumanReviews(
  filters: AdminReviewFilters,
): Promise<AdminReviewListResponse | null> {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.module) params.set('module', filters.module);
  if (filters.priority) params.set('priority', filters.priority);
  if (filters.mine) params.set('mine', 'true');
  params.set('page', String(filters.page ?? 1));
  return apiGet<AdminReviewListResponse>(`/admin/human-reviews?${params.toString()}`, {
    errorMessage: 'Failed to load review queue',
    context: 'getAdminHumanReviews',
  });
}

export async function getAdminReviewDetail(id: string): Promise<AdminReviewDetail | null> {
  return apiGet<AdminReviewDetail>(`/admin/human-reviews/${id}`, {
    errorMessage: 'Failed to load review',
    context: 'getAdminReviewDetail',
  });
}

export async function claimReview(id: string): Promise<unknown> {
  return apiPost(`/admin/human-reviews/${id}/claim`, undefined, {
    errorMessage: 'Failed to claim review',
    context: 'claimReview',
    throwOnError: true,
  });
}

export async function completeReview(
  id: string,
  payload: CompleteReviewPayload,
): Promise<AdminReviewDetail | null> {
  return apiPost<AdminReviewDetail>(`/admin/human-reviews/${id}/complete`, payload, {
    errorMessage: 'Failed to deliver review',
    context: 'completeReview',
    throwOnError: true,
  });
}
